/**
 * BannerListPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  ModalLayout,
  InputFormTable,
  Text,
} from 'components';
import {
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Flex,
  Image,
  Input,
  Radio,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { STORE_URL } from 'utils';
import { MinusOutlined } from '@ant-design/icons';
import { downloadXlsx } from 'utils/XlsxDownloader';

const BANNER_TYPE_COLOR = {
  PC: 'purple',
  MOBILE: 'blue',
  KIOSK: 'green',
};

const INITIAL_FILTER_ITEM = {
  banner_type: null,
  grade_show: null,
};

const DATE_FORMAT = 'YYYY.MM.DD';

const BannerListPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    banners,
    grades,
    inputValue,
    isLoading,
    onChange,
    onSubmit,
    setIsLoading,
    modalType,
    onModal,
    // dataList,
    // setDataList,
  } = props;

  /* ===== STATE ===== */
  /* ===== VARIABLE ===== */
  const fileInputRef = React.useRef(null);
  const [filterItem, setFilterItem] = React.useState(INITIAL_FILTER_ITEM);
  const [filterList, setFilterList] = React.useState([...banners]);
  const [tap, setTap] = React.useState('PC');

  const [sortItems, setSortItems] = React.useState([]);

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  /**
   * 필터 아이템
   * --
   */
  const formItem = [
    {
      label: '구분',
      form: [
        {
          input: (
            <Radio.Group
              value={filterItem['banner_type']}
              onChange={(e) =>
                setFilterItem((prev) => ({
                  ...prev,
                  banner_type: e.target.value,
                }))
              }
            >
              <Radio value={null}>전체</Radio>
              <Radio value={'PC'}>PC</Radio>
              <Radio value={'MOBILE'}>모바일</Radio>
              <Radio value={'KIOSK'}>키오스크</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '노출등급',
      form: [
        {
          input: (
            <React.Fragment>
              <Checkbox
                checked={filterItem['grade_show'] === null ? true : false}
                onChange={(e) =>
                  setFilterItem((prev) => ({
                    ...prev,
                    grade_show: null,
                  }))
                }
              >
                전체
              </Checkbox>
              <Checkbox.Group
                value={filterItem['grade_show']}
                options={[
                  ...grades.map((grade) => ({
                    label: grade.grade_name,
                    value: grade.grade_id,
                  })),
                ]}
                onChange={(e) =>
                  setFilterItem((prev) => ({
                    ...prev,
                    grade_show: e,
                  }))
                }
              />
            </React.Fragment>
          ),
        },
      ],
    },
  ];

  /**
   * 폼
   * --
   */
  const form = React.useMemo(() => {
    return [
      {
        label: '배너 명',
        required: true,
        form: [
          {
            input: (
              <Input
                placeholder="배너의 제목을 입력해주세요"
                style={{ width: 400 }}
                value={inputValue.banner_name}
                onChange={(e) => onChange('banner_name', e.target.value)}
                maxLength={30}
                showCount
              />
            ),
          },
        ],
      },
      {
        label: '노출 플랫폼',
        required: true,
        form: [
          {
            input: (
              <Radio.Group
                value={inputValue['banner_type']}
                onChange={(e) => onChange('banner_type', e.target.value)}
              >
                <Radio value={'PC'}>PC</Radio>
                <Radio value={'MOBILE'}>모바일</Radio>
                <Radio value={'KIOSK'}>키오스크</Radio>
              </Radio.Group>
            ),
          },
        ],
      },
      {
        label: '노출일',
        required: true,
        form: [
          {
            input: (
              <Flex gap={4} align="center">
                <DatePicker
                  value={
                    inputValue.banner_start_date
                      ? dayjs.unix(inputValue.banner_start_date)
                      : ''
                  }
                  format={DATE_FORMAT}
                  onChange={(e) =>
                    onChange(
                      'banner_start_date',
                      dayjs(e).startOf('day').unix()
                    )
                  }
                  placeholder="노출 시작일"
                  maxDate={dayjs.unix(inputValue.banner_end_date)}
                />
                <p>~</p>
                <DatePicker
                  value={
                    inputValue.banner_end_date
                      ? dayjs.unix(inputValue.banner_end_date)
                      : ''
                  }
                  format={DATE_FORMAT}
                  onChange={(e) =>
                    onChange('banner_end_date', dayjs(e).endOf('day').unix())
                  }
                  placeholder="노출 종료일"
                  minDate={dayjs.unix(inputValue.banner_start_date)}
                />
              </Flex>
            ),
          },
        ],
      },

      {
        label: '노출 등급',
        required: true,
        form: [
          {
            input: (
              <Flex gap={8}>
                <Checkbox
                  checked={inputValue['grade_show'] === null ? true : false}
                  onChange={(e) => onChange('grade_show', null)}
                >
                  전체
                </Checkbox>
                <Checkbox.Group
                  value={inputValue['grade_show']}
                  options={[
                    ...grades.map((grade) => ({
                      label: grade.grade_name,
                      value: grade.grade_id,
                    })),
                  ]}
                  onChange={(e) => onChange('grade_show', e)}
                />
              </Flex>
            ),
          },
        ],
      },

      {
        label: '파일',
        required: true,
        form: [
          {
            input: inputValue.banner_url ? (
              typeof inputValue.banner_url === 'string' ? (
                <div style={{ position: 'relative', top: 0, left: 0 }}>
                  {inputValue.content_type === 'IMG' ? (
                    <Image
                      width={300}
                      src={`${STORE_URL}/${inputValue.banner_url}`}
                    />
                  ) : (
                    <video
                      width={300}
                      src={`${STORE_URL}/${inputValue.banner_url}`}
                      muted
                      loop
                      autoPlay
                    />
                  )}

                  <Button
                    danger
                    size="small"
                    icon={<MinusOutlined />}
                    style={{ position: 'absolute', top: -10, right: -10 }}
                    onClick={() => onChange('banner_url', null)}
                  />
                </div>
              ) : (
                <div style={{ position: 'relative', top: 0, left: 0 }}>
                  {inputValue.banner_url.type.includes('video') ? (
                    <video
                      width={300}
                      src={URL.createObjectURL(inputValue.banner_url)}
                      muted
                      loop
                      autoPlay
                    />
                  ) : (
                    <Image
                      width={300}
                      src={URL.createObjectURL(inputValue.banner_url)}
                    />
                  )}

                  <Button
                    danger
                    size="small"
                    icon={<MinusOutlined />}
                    style={{ position: 'absolute', top: -10, right: -10 }}
                    onClick={() => onChange('banner_url', null)}
                  />
                </div>
              )
            ) : (
              <React.Fragment>
                <input
                  ref={fileInputRef}
                  id="upload"
                  type="file"
                  multiple={true}
                  accept={
                    inputValue.banner_type !== 'PC'
                      ? 'image/*'
                      : 'image/*, video/*'
                  }
                  onChange={(e) => onChange('banner_url', e.target.files[0])}
                />
                <Button onClick={() => fileInputRef.current.click()}>
                  파일선택
                </Button>
              </React.Fragment>
            ),
          },
        ],
      },

      ...(inputValue.banner_type === 'PC'
        ? [
            {
              label: '새창여부',
              form: [
                {
                  input: (
                    <Radio.Group
                      value={inputValue['is_new_window']}
                      onChange={(e) =>
                        onChange('is_new_window', e.target.value)
                      }
                    >
                      <Radio value={'Y'}>사용</Radio>
                      <Radio value={'N'}>사용안함</Radio>
                    </Radio.Group>
                  ),
                },
              ],
            },
            {
              label: '렌더 URL',
              form: [
                {
                  input: (
                    <Input
                      placeholder="이동시킬 URL을 입력해주세요"
                      style={{ width: 400 }}
                      value={inputValue.render_url}
                      onChange={(e) => onChange('render_url', e.target.value)}
                    />
                  ),
                },
              ],
            },
          ]
        : []),
    ];
    // eslint-disable-next-line
  }, [inputValue, banners, grades]);

  /**
   * 테이블 컬럼
   * --
   */
  const columns = React.useMemo(() => {
    return [
      {
        title: 'No',
        dataIndex: 'banner_id',
        key: 'banner_id',
        width: 75,
        sorter: (a, b) => a?.banner_id - b?.banner_id,
      },
      {
        title: '구분위치',
        dataIndex: 'banner_type',
        key: 'banner_type',
        width: 95,
        render: (text) => <Tag color={BANNER_TYPE_COLOR[text]}>{text}</Tag>,
      },
      {
        title: '배너',
        dataIndex: 'banner_url',
        key: 'banner_url',
        width: 110,
        render: (text, record) =>
          record.content_type === 'IMG' ? (
            <Image
              src={`${STORE_URL}/${text}`}
              width={80}
              height={50}
              preview={false}
            />
          ) : (
            <video
              width={80}
              height={50}
              src={`${STORE_URL}/${text}`}
              muted
              loop
              autoPlay
            />
          ),
      },
      {
        title: '배너 명',
        dataIndex: 'banner_name',
        key: 'banner_name',
        width: 300,
      },
      {
        title: '배너 타입',
        dataIndex: 'content_type',
        key: 'content_type',
        width: 100,
        align: 'center',
        render: (text) => <Tag>{text}</Tag>,
        filters: [
          {
            text: '이미지',
            value: 'IMG',
          },
          {
            text: '비디오',
            value: 'VIDEO',
          },
        ],
        onFilter: (value, { content_type }) => content_type === value,
      },
      {
        title: '노출여부',
        dataIndex: 'is_show',
        key: 'is_show',
        width: 100,
        align: 'center',
        render: (text) => (
          <Tag color={text === 'Y' ? 'processing' : 'default'}>
            {text === 'Y' ? '노출' : '숨김'}
          </Tag>
        ),
        filters: [
          {
            text: '노출',
            value: 'Y',
          },
          {
            text: '숨김',
            value: 'ㅜ',
          },
        ],
        onFilter: (value, { is_show }) => is_show === value,
      },
      {
        title: '노출등급',
        dataIndex: 'grade_show',
        key: 'grade_show',
        width: 185,
        align: 'center',
        render: (text) =>
          !text
            ? '전체노출'
            : text.map((grade) => <Tag>{grade.grade_name}</Tag>),
      },
      {
        title: '노출순서',
        dataIndex: 'order',
        key: 'order',
        width: 100,
        align: 'center',
        sorter: (a, b) => a?.order - b?.order,
      },
      {
        title: '노출 시작일',
        dataIndex: 'banner_start_date',
        key: 'banner_start_date',
        width: 135,
        align: 'center',
        render: (text) => dayjs.unix(text).format('YYYY.MM.DD'),
        sorter: (a, b) => a?.banner_start_date - b?.banner_start_date,
      },
      {
        title: '노출 종료일',
        dataIndex: 'banner_end_date',
        key: 'banner_end_date',
        width: 135,
        align: 'center',
        render: (text) => dayjs.unix(text).format('YYYY.MM.DD'),
        sorter: (a, b) => a?.banner_end_date - b?.banner_end_date,
      },
      {
        title: '새창여부',
        dataIndex: 'is_new_window',
        key: 'is_new_window',
        width: 100,
        align: 'center',
        filters: [
          {
            text: '새창',
            value: 'Y',
          },
          {
            text: '현재창',
            value: 'N',
          },
        ],
        onFilter: (value, { is_new_window }) => is_new_window === value,
      },
      {
        title: '랜딩 URL',
        dataIndex: 'render_url',
        key: 'render_url',
        width: 185,
        align: 'center',
        render: (text) =>
          text ? (
            <Button
              type="link"
              onClick={() => window.open(text)}
              style={{ padding: 0 }}
            >
              {text}
            </Button>
          ) : (
            '-'
          ),
      },
      {
        title: '등록일',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 150,
        align: 'center',
        render: (text) => dayjs.unix(text).format('YYYY.MM.DD HH:mm'),
        sorter: (a, b) => a?.created_at - b?.created_at,
      },
    ];
  }, []);

  /* ===== FUNCTION ===== */
  /**
   * 배너 검색
   * --
   */
  const handleSearchBanner = (type) => {
    setIsLoading(true);

    let newList = [];

    switch (type) {
      case 'RESET':
        newList = [...banners];
        setFilterItem(INITIAL_FILTER_ITEM);
        break;
      case 'SEARCH':
        newList = [...banners];

        const { banner_type, grade_show } = filterItem;

        if (banner_type) {
          newList = newList.filter(
            (banner) => banner['banner_type'] === banner_type
          );
        }

        if (grade_show) {
          newList = newList.filter((banner) =>
            banner.grade_show?.some((grade) =>
              grade_show.includes(grade.grade_id)
            )
          );
        }
        break;
      default:
        break;
    }

    setTimeout(() => {
      setFilterList(newList);
      setIsLoading(false);
    }, 1000);
  };

  /**
   * 아이템 순서 변경
   * --
   * @param {array} arr
   * @param {number} fromIndex
   * @param {number} toIndex
   */
  const orderChange = (arr, fromIndex, toIndex) => {
    // 순서 변경할 아이템 복사
    const itemToMove = arr[fromIndex];

    // 이동시킬 아이템 제거
    const tempArr = [...arr.slice(0, fromIndex), ...arr.slice(fromIndex + 1)];

    // 아이템 삽입
    const newArr = tempArr.toSpliced(toIndex, 0, itemToMove);
    setSortItems(newArr);
  };

  /* ===== HOOKS ===== */
  React.useEffect(() => {
    handleSearchBanner('SEARCH');

    // eslint-disable-next-line
  }, [banners]);

  /**
   * 순서정렬 아이템 정제
   * --
   */
  React.useEffect(() => {
    if (modalType !== 'SORT') return;

    const filterItems = banners
      ?.filter((_item) => _item?.banner_type === tap && _item?.is_show === 'Y')
      ?.sort((a, b) => a?.order - b?.order);

    setSortItems(filterItems);
  }, [banners, tap, modalType]);

  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row>
            <Col x={24}>
              <Title style={{ ...styles.title }}>배너 관리</Title>

              {/* 검색필터 */}
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor={'none'}
                style={{ marginBottom: 20 }}
              >
                <ExpandableContainer borderRadius={4} padding={20}>
                  {formItem.map((_item, _index) => (
                    <div
                      key={`form_${_index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 0px',
                      }}
                    >
                      <div
                        style={{ width: 120, fontSize: 13, fontWeight: 400 }}
                      >
                        <label>{_item.label}</label>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {_item.form.map((_subitem, _subkey) => (
                          <React.Fragment key={`input_${_subkey}`}>
                            {_subitem.input}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </ExpandableContainer>

                {/* 검색/초기화 버튼 */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 20,
                  }}
                >
                  <Button
                    size="large"
                    type="primary"
                    style={{
                      ...styles.button,
                      background: 'var(--primary-color)',
                    }}
                    onClick={() => handleSearchBanner('SEARCH')}
                  >
                    검색
                  </Button>
                  <Button
                    size="large"
                    style={{ ...styles.button }}
                    onClick={() => handleSearchBanner('RESET')}
                  >
                    초기화
                  </Button>
                </div>
                {/* 검색/초기화 버튼 끝 */}
              </Content>
              {/* 검색필터 끝 */}

              {/* 검색결과 */}
              <Content maxWidth={'100%'} borderRadius={4} padding={20}>
                <Title
                  size={5}
                  subButton={
                    <div>
                      <Button
                        style={{ marginRight: 5 }}
                        onClick={() => onModal('create')}
                      >
                        새 배너
                      </Button>
                      <Button
                        style={{ marginRight: 5 }}
                        onClick={() => onModal('sort', filterList)}
                      >
                        순서변경
                      </Button>
                      <Button
                        style={{ marginRight: 5 }}
                        onClick={() =>
                          downloadXlsx({
                            columns: columns,
                            rows: filterList?.map((_item) => ({
                              ..._item,
                              banner_type:
                                _item?.banner_type === 'ALL'
                                  ? '전체'
                                  : _item?.banner_type === 'PC'
                                  ? '웹'
                                  : _item?.banner_type === 'MOBILE'
                                  ? '모바일'
                                  : '키오스크',
                              grade_show: _item?.grade_show
                                ?.map((_item) => _item?.grade_name)
                                .join(', '),
                            })),
                            fileName: '배너',
                          })
                        }
                      >
                        엑셀로 다운로드
                      </Button>
                    </div>
                  }
                >
                  <span>
                    총 <span>{filterList.length}</span>개
                  </span>
                </Title>
                <br />

                <Table
                  dataSource={filterList}
                  columns={columns}
                  scroll={{ x: 'max-content' }}
                  onRow={(record) => {
                    return {
                      onClick: () => onModal('detail', record),
                    };
                  }}
                />
              </Content>
              {/* 검색결과 끝 */}
            </Col>
          </Row>
        </Content>
      </Spin>

      {/* SECTION: 상세정보 모달 */}
      <ModalLayout
        title="배너상세정보"
        type={'modal'}
        open={modalType && modalType !== 'SORT'}
        onCancel={() => onModal('close')}
        centered
        closable
        footer={
          <Content maxWidth={'100%'} padding={0}>
            <Row>
              <Col x={8} style={{ textAlign: 'left', display: 'flex' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <p
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      lineHeight: '133%',
                      paddingRight: 5,
                    }}
                  >
                    노출여부
                  </p>
                  <Switch
                    value={inputValue.is_show === 'Y' ? true : false}
                    onChange={(e) => onChange('is_show', e ? 'Y' : 'N')}
                  />
                </div>
              </Col>
              <Col x={8} style={{ textAlign: 'center' }}></Col>
              <Col x={8}>
                {modalType !== 'CREATE' ? (
                  <Button
                    danger
                    style={{
                      ...styles.button,
                    }}
                    onClick={() => onSubmit('delete')}
                  >
                    삭제
                  </Button>
                ) : null}

                <Button
                  type="primary"
                  style={{
                    ...styles.button,
                    background: 'var(--primary-color)',
                  }}
                  onClick={() =>
                    onSubmit(modalType === 'CREATE' ? 'create' : 'update')
                  }
                >
                  저장
                </Button>
              </Col>
            </Row>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={'10px 0'}>
          <Row>
            <Col x={24}>
              <InputFormTable form={form} labelWidth={148} />
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* SECTION: 순서변경 모달 */}
      <ModalLayout
        type={'modal'}
        open={modalType && modalType === 'SORT'}
        onCancel={() => onModal('close')}
        centered
        closable
        width={500}
        footer={
          <Content maxWidth={'100%'} padding={0}>
            <Row>
              <Col x={24}>
                <Flex justify="flex-end">
                  <Button
                    type="primary"
                    style={{
                      ...styles.button,
                      background: 'var(--primary-color)',
                    }}
                    onClick={() => onSubmit('sort', sortItems, tap)}
                  >
                    저장
                  </Button>
                </Flex>
              </Col>
            </Row>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={'20px 0'}>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                <Radio.Group
                  value={tap}
                  options={[
                    { label: 'PC', value: 'PC' },
                    { label: '모바일', value: 'MOBILE' },
                    { label: '키오스크', value: 'KIOSK' },
                  ]}
                  onChange={(e) => setTap(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                />

                <Flex vertical gap={24} style={{ padding: '12px 0px' }}>
                  {sortItems?.length ? (
                    sortItems?.map((_item, _index) => (
                      <Flex
                        key={`banner-${_item.banner_id}`}
                        justify="space-between"
                        align="center"
                      >
                        <Flex align="center" gap={12}>
                          <div
                            style={{
                              width: 80,
                              height: 80,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid #BDBDBD',
                            }}
                          >
                            {_item?.content_type === 'IMG' ? (
                              <Image
                                src={`${STORE_URL}/${_item?.banner_url}`}
                                width={80}
                                height={'100%'}
                                style={{ objectFit: 'cover' }}
                              />
                            ) : (
                              <video
                                src={`${STORE_URL}/${_item?.banner_url}`}
                                width={80}
                                muted
                                loop
                                autoPlay
                                style={{ objectFit: 'cover' }}
                              />
                            )}
                          </div>
                          <Text weight={700}>{_item?.banner_name}</Text>
                        </Flex>

                        <Select
                          value={_index}
                          options={new Array(sortItems?.length)
                            .fill(null)
                            .map((_, index) => ({
                              label: `${index + 1}번`,
                              value: index,
                            }))}
                          onChange={(e) =>
                            orderChange(sortItems, _index, Number(e))
                          }
                        />
                      </Flex>
                    ))
                  ) : (
                    <Empty description={'노출중인 배너가 존재하지 않습니다.'} />
                  )}
                </Flex>
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>
    </>
  );
};

export default BannerListPresenter;
